/**
 * WindowHost — mounts each enabled window inside a draggable, minimizable,
 * closable ToolWindow.
 *
 * Generalized from the kit's old `ui/debug/DebugHUDHost.tsx` (Phase 1 of the
 * UI-framework arc). Reads visibility state from WindowManagerContext. Each
 * ToolWindow keeps its FROZEN persistent `id` (e.g. `debugtool_latency`) so
 * position + size + minimized state survive reloads via StorageService — the
 * geometry-key prefix MUST stay `debugtool_` or users lose their saved layouts.
 *
 * Closing a window via the × button clears its enabled flag in the context
 * (and therefore in localStorage). Re-opening from WindowLauncher restores the
 * persisted position/size.
 *
 * Each window's content component is always-on once mounted — the host decides
 * when it lives or dies. Tool-kind windows are gated to the debug surface +
 * displayMode (preserved exactly); later kinds slot into the same render path.
 */

import React, { useEffect, useRef } from 'react';
import { registerWindowRestore } from './windowBridge';
import { PLAYER_WINDOW_PRESENTATION } from './playerWindowPresentation';
import { Z } from '../theme';
import { ToolWindow, type ToolWindowHandle } from '../components/ToolWindow';
import { TomeWindow } from '../components/TomeWindow';
import { zoneTomeVibe } from '../primitives/tome/zoneTomeVibes';
import { keyBindingsNavigationStore } from '../Windows/KeyBindings/keyBindingsNavigationStore';
import { settingsNavigationStore } from '../Windows/Settings/settingsNavigationStore';
import { PLAYER_THEME_VARS } from '../theme';
import { useWindows } from './WindowManagerContext';
import type { WindowDef, WindowId } from './types';
import { CONTENT_BY_ID } from './content';
import { DebugSuiteContainer } from '../debug/DebugSuiteContainer';
import { isDebugSurface } from '../../../shared/diagnostics/isDebugSurface';

/**
 * The persisted ToolWindow geometry key for a window.
 *
 * Tool windows use `debugtool_<id>` (frozen from the 🛠 kit). Diagnostic
 * windows folded in Phase 3 keep the EXACT `toolwindow_<oldId>` key the
 * standalone panel used before the fold — so DJ's saved drag positions /
 * sizes / minimized state survive the migration. ToolWindow prefixes every
 * key it's given with `toolwindow_`, so we pass the bare old id here.
 */
const FROZEN_DIAGNOSTIC_TOOLWINDOW_ID: Record<string, string> = {
  targetAbilityCheck: 'target-ability-check',
  abilityLifecycle: 'ability-lifecycle-monitor',
  visualLifecycle: 'visual-lifecycle-monitor',
  targetLifecycle: 'target-lifecycle-monitor',
  abilityTestRig: 'ability-test-rig',
  netobsPacketLog: 'netobs-packet-log',
  netobsLatencyHeatmap: 'netobs-latency-heatmap',
  schemaGhost: 'schema-ghost',
};

/**
 * Gameplay windows folded in Phase 2 keep the EXACT bare id their standalone
 * window passed to ToolWindow before the fold (ToolWindow prefixes it with
 * `toolwindow_`), so DJ's saved drag positions / sizes / minimized state
 * survive the migration. Mirror of FROZEN_DIAGNOSTIC_TOOLWINDOW_ID.
 *   - settings → 'settings-window' (the old SettingsWindow ToolWindow id).
 *   - twitchPiP → 'twitch-pip' (the old TwitchPiPWindow ToolWindow id).
 *   - bank → 'bank-window' (the old BankWindow ToolWindow id).
 *   - crafting → 'crafting-window' (the old CraftingWindow ToolWindow id).
 *   - lobbyTools → 'lobby-tools' (the old LobbyToolsPanel ToolWindow id).
 *   - trade → 'trade-window' (the old TradeWindow ToolWindow id).
 *   - party → 'party-panel' (the old PartyPanel ToolWindow id).
 *   - jobs → 'jobs-window' (the old JobsWindow ToolWindow id).
 *   - talents → 'talents-window' (the old TalentsWindow ToolWindow id).
 *   - equipment → 'equipment-window' (now the combined Character window).
 *   - inventory → 'inventory-window' (the old InventoryUI ToolWindow id).
 *   - spells → 'ability-browser-window' (the old AbilityBrowser ToolWindow id).
 *   - quests → 'quest-log-window' (the old QuestUI ToolWindow id).
 *   - simulationPanel → 'simulation-control' (the old SimulationControlPanel ToolWindow id).
 */
const FROZEN_GAMEPLAY_TOOLWINDOW_ID: Record<string, string> = {
  settings: 'settings-window',
  twitchPiP: 'twitch-pip',
  bank: 'bank-window',
  crafting: 'crafting-window',
  lobbyTools: 'lobby-tools',
  trade: 'trade-window',
  party: 'party-panel',
  jobs: 'jobs-window',
  talents: 'talents-window',
  equipment: 'equipment-window',
  inventory: 'inventory-window',
  spells: 'ability-browser-window',
  quests: 'quest-log-window',
  simulationPanel: 'simulation-control',
  qaPanel: 'qa-panel',
  contractDashboard: 'contract-dashboard',
  // HUD fold (Phase 4, 2026-06-12) — ids the HUD-mounted ToolWindows used.
  cameraControl: 'camera-control',
  simulationTools: 'simulation-tools',
  mapTools: 'map-tools',
};

function toolWindowId(def: WindowDef): string {
  if (def.kind === 'diagnostic') {
    return FROZEN_DIAGNOSTIC_TOOLWINDOW_ID[def.id] ?? def.id;
  }
  if (def.kind === 'gameplay') {
    return FROZEN_GAMEPLAY_TOOLWINDOW_ID[def.id] ?? def.id;
  }
  return `debugtool_${def.id}`;
}

interface ManagedWindowProps {
  def: WindowDef;
  onClose: () => void;
}

const ManagedWindow: React.FC<ManagedWindowProps> = ({ def, onClose }) => {
  const handle = useRef<ToolWindowHandle>(null);
  useEffect(() => registerWindowRestore(def.id, () => handle.current?.restore()), [def.id]);
  const presentation = def.kind === 'gameplay' && def.category === 'game' ? PLAYER_WINDOW_PRESENTATION[def.id] : undefined;
  const Content = CONTENT_BY_ID[def.id];
  if (!Content) return null;
  if (def.presentation === 'overlay') {
    // Bare full-screen content, no ToolWindow chrome (e.g. the Map Picker).
    // The host root is pointerEvents:none, so the overlay re-enables it.
    return (
      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'auto' }}>
        <Content />
      </div>
    );
  }
  if (presentation?.presentation === 'tome' || def.presentation === 'tome') {
    const currentZone = (globalThis as { nm?: { getRoom?: () => { state?: { mapId?: string } } | null } })
      .nm?.getRoom?.()?.state?.mapId ?? null;
    const handleClose = () => {
      if (def.id === 'keyBindings') {
        keyBindingsNavigationStore.restoreFocusOnClose();
      } else if (def.id === 'settings') {
        settingsNavigationStore.restoreFocusOnClose();
      }
      onClose();
    };
    return (
      <div
        data-controller-surface="window"
        data-controller-window-id={def.id}
        style={{ display: 'contents', ...PLAYER_THEME_VARS }}
      >
        <TomeWindow
          ref={handle}
          id={toolWindowId(def)}
          title={presentation?.title ?? def.label}
          eyebrow={presentation?.eyebrow}
          vibe={presentation?.vibe ?? zoneTomeVibe(currentZone)}
          scrollable={presentation?.scrollable ?? true}
          isOpen
          onClose={handleClose}
          defaultPosition={def.defaultPosition}
          defaultSize={presentation?.size ?? def.defaultSize}
          minSize={def.minSize ?? { width: 340, height: 300 }}
          maxSize={def.maxSize ?? { width: 1400, height: 1000 }}
          resizable
          persistState={def.persistGeometry ?? true}
          restoreOnOpen={def.id === 'spells' || def.id === 'quests'}
          bringToFrontOnDrag
          zIndex={def.zIndex}
          testId={`tomewindow_${def.id}`}
        >
          <Content />
        </TomeWindow>
      </div>
    );
  }
  return (
    <div
      data-controller-surface="window"
      data-controller-window-id={def.id}
      style={{ display: 'contents', ...(def.kind === 'gameplay' && def.category === 'game' ? PLAYER_THEME_VARS : {}) }}
    >
      <ToolWindow
        ref={handle}
        id={toolWindowId(def)}
        title={presentation?.title ?? def.label}
        className={def.kind === 'gameplay' && def.category === 'game' ? 'toolwindow--player' : ''}
        icon={def.icon}
        isOpen
        onClose={onClose}
        defaultPosition={def.defaultPosition}
        defaultSize={presentation?.size ?? def.defaultSize}
        minSize={def.minSize ?? { width: 220, height: 80 }}
        maxSize={def.maxSize ?? { width: presentation ? 1000 : 600, height: 800 }}
        collapsible
        resizable
        minimizable
        persistState={def.persistGeometry ?? true}
        restoreOnOpen={def.id === 'spells' || def.id === 'quests'}
        bringToFrontOnDrag
        zIndex={def.zIndex}
      >
        <Content />
      </ToolWindow>
    </div>
  );
};

export const WindowHost: React.FC = () => {
  const { widgets, windows, setEnabled, displayMode } = useWindows();

  // Surface guard — the floating windows are a /debug-surface concern only.
  // The state (`widgets[id].enabled`) is a once-at-mount snapshot owned by a
  // provider that lives for the whole BrowserRouter SPA session, so a flag
  // turned on while on /debug (by the WindowLauncher or a playtest rig)
  // survives a client-side navigation onto a clean surface like /pve and would
  // otherwise keep rendering its floating window there. `loadInitialState`
  // already scopes the INITIAL state to the surface; this keeps the render
  // honest if the state is ever flipped on at runtime off-surface. DJ
  // 2026-05-29: "if I pick pve it shouldn't load all our stutter detect." Same
  // predicate the netobs reporters + playtest rig use, so all three lanes
  // agree on what a debug surface is.
  const onDebugSurface = isDebugSurface();

  // Two deliberately separate surfaces (/debug-surface-only — see guard):
  //   - Floating windows use widgets[id].enabled in floating mode.
  //   - Hub sections use hubWidgets[id].enabled inside the hub container.
  // There is no combined presentation: selecting a hub row must not spawn a
  // second draggable copy of the same tool.
  const showHub =
    onDebugSurface && displayMode.kind === 'hub';
  const showFloating =
    onDebugSurface && displayMode.kind === 'floating';

  // Diagnostic-kind windows (Phase 3 fold) render on EVERY surface — they're
  // observational panels DJ opens to watch live combat/netcode on /brawl etc.,
  // not /debug-only stutter-detect tooling. They ignore the hub/floating
  // displayMode (always floating) and the debug-surface gate. This is the only
  // per-kind behavioral difference the discriminated union encodes; everything
  // else (persistence, URL/surface seeding, launcher row) is shared.
  const isOpen = (id: WindowId) => widgets[id]?.enabled ?? false;
  const renderWindow = (def: WindowDef) =>
    isOpen(def.id) ? (
      <ManagedWindow
        key={def.id}
        def={def}
        onClose={() => setEnabled(def.id, false)}
      />
    ) : null;

  // A fixed host creates its own stacking context: raising a child window
  // cannot put it above the sibling target HUD unless the host is above it.
  return (
    <div data-testid="debug-hud-host" data-mode={displayMode.kind} data-hub-open={displayMode.kind === 'hub' ? 'true' : 'false'} style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: Z.window }}>
      {showHub && <DebugSuiteContainer />}
      {/* Tool windows — debug-surface + displayMode gated. */}
      {showFloating && windows.filter((d) => d.kind === 'tool').map(renderWindow)}
      {/* Diagnostic windows — every surface, always floating. */}
      {windows.filter((d) => d.kind === 'diagnostic').map(renderWindow)}
      {/* Gameplay windows (Phase 2 fold) — every surface, always floating;
          opened from the QUICK MENU via windowBridge. Same render path as
          diagnostic (no debug-surface gate). */}
      {windows.filter((d) => d.kind === 'gameplay').map(renderWindow)}
    </div>
  );
};
